import { useState } from 'react';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Star, UserPlus, Pencil, X, Check } from 'lucide-react';
import { z } from 'zod';
import { client } from '@/lib/api';
import {
  createVendorContactSchema,
  updateVendorContactSchema,
  type CreateVendorContactInput,
  type UpdateVendorContactInput,
} from '@everplan/shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';

// ---------------------------------------------------------------------------
// Local types
// ---------------------------------------------------------------------------

interface Contact {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  role: string | null;
  isPrimary: boolean;
  deactivatedAt: string | null;
}

interface Props {
  contacts: Contact[];
  vendorId: string;
}

// Zod input type (before coerce)
type CreateContactFormInput = z.input<typeof createVendorContactSchema>;

// ---------------------------------------------------------------------------
// Inline add-contact form
// ---------------------------------------------------------------------------

function AddContactForm({
  vendorId,
  onCancel,
}: {
  vendorId: string;
  onCancel: () => void;
}) {
  const queryClient = useQueryClient();

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<CreateContactFormInput, unknown, CreateVendorContactInput>({
    resolver: zodResolver(createVendorContactSchema),
    defaultValues: {
      name: '',
      email: null,
      phone: null,
      role: null,
      isPrimary: false,
    },
  });

  const mutation = useMutation({
    mutationFn: async (data: CreateVendorContactInput) => {
      const res = await client.api.vendors[':vendorId'].contacts.$post({
        param: { vendorId },
        json: data,
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as { error?: string }).error ?? 'Failed to add contact');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendor', vendorId] });
      reset();
      onCancel();
    },
  });

  return (
    <form
      onSubmit={handleSubmit((data) => mutation.mutate(data))}
      className="space-y-3 rounded-xl border border-border bg-muted/40 p-4"
    >
      <p className="label-caps text-xs text-muted-foreground">New contact</p>

      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1">
          <Label htmlFor="nc-name">
            <span className="label-caps text-xs text-muted-foreground">Name *</span>
          </Label>
          <Input
            id="nc-name"
            placeholder="Jane Smith"
            {...register('name')}
            aria-invalid={!!errors.name}
          />
          {errors.name && <p className="text-xs text-destructive">{errors.name.message}</p>}
        </div>

        <div className="space-y-1">
          <Label htmlFor="nc-role">
            <span className="label-caps text-xs text-muted-foreground">Role</span>
          </Label>
          <Input id="nc-role" placeholder="Sales rep" {...register('role')} />
        </div>

        <div className="space-y-1">
          <Label htmlFor="nc-email">
            <span className="label-caps text-xs text-muted-foreground">Email</span>
          </Label>
          <Input
            id="nc-email"
            type="email"
            placeholder="jane@example.com"
            {...register('email')}
          />
          {errors.email && (
            <p className="text-xs text-destructive">{errors.email.message as string}</p>
          )}
        </div>

        <div className="space-y-1">
          <Label htmlFor="nc-phone">
            <span className="label-caps text-xs text-muted-foreground">Phone</span>
          </Label>
          <Input id="nc-phone" type="tel" placeholder="(555) 123-4567" {...register('phone')} />
        </div>
      </div>

      <label className="flex cursor-pointer items-center gap-2 text-sm">
        <Controller
          control={control}
          name="isPrimary"
          render={({ field }) => (
            <Checkbox
              checked={!!field.value}
              onCheckedChange={(v) => field.onChange(v === true)}
            />
          )}
        />
        Primary contact
      </label>

      {mutation.isError && (
        <p className="text-xs text-destructive">
          {mutation.error?.message ?? 'Something went wrong.'}
        </p>
      )}

      <div className="flex gap-2">
        <Button type="submit" size="sm" disabled={mutation.isPending}>
          {mutation.isPending ? 'Adding…' : 'Add contact'}
        </Button>
        <Button type="button" size="sm" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Inline edit-contact form
// ---------------------------------------------------------------------------

function EditContactForm({
  contact,
  vendorId,
  onCancel,
}: {
  contact: Contact;
  vendorId: string;
  onCancel: () => void;
}) {
  const queryClient = useQueryClient();

  const { register, handleSubmit, control, formState: { errors } } = useForm<UpdateVendorContactInput>({
    resolver: zodResolver(updateVendorContactSchema),
    defaultValues: {
      name: contact.name,
      email: contact.email,
      phone: contact.phone,
      role: contact.role,
      isPrimary: contact.isPrimary,
    },
  });

  const mutation = useMutation({
    mutationFn: async (data: UpdateVendorContactInput) => {
      const res = await client.api['vendor-contacts'][':id'].$put({
        param: { id: contact.id },
        json: data,
      });
      if (!res.ok) throw new Error('Failed to update contact');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendor', vendorId] });
      onCancel();
    },
  });

  return (
    <form
      onSubmit={handleSubmit((data) => mutation.mutate(data))}
      className="space-y-3 rounded-xl border border-border bg-muted/40 p-3"
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1">
          <Label htmlFor={`ec-name-${contact.id}`}>
            <span className="label-caps text-xs text-muted-foreground">Name *</span>
          </Label>
          <Input
            id={`ec-name-${contact.id}`}
            {...register('name')}
            aria-invalid={!!errors.name}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor={`ec-role-${contact.id}`}>
            <span className="label-caps text-xs text-muted-foreground">Role</span>
          </Label>
          <Input id={`ec-role-${contact.id}`} {...register('role')} />
        </div>
        <div className="space-y-1">
          <Label htmlFor={`ec-email-${contact.id}`}>
            <span className="label-caps text-xs text-muted-foreground">Email</span>
          </Label>
          <Input id={`ec-email-${contact.id}`} type="email" {...register('email')} />
        </div>
        <div className="space-y-1">
          <Label htmlFor={`ec-phone-${contact.id}`}>
            <span className="label-caps text-xs text-muted-foreground">Phone</span>
          </Label>
          <Input id={`ec-phone-${contact.id}`} type="tel" {...register('phone')} />
        </div>
      </div>

      <label className="flex cursor-pointer items-center gap-2 text-sm">
        <Controller
          control={control}
          name="isPrimary"
          render={({ field }) => (
            <Checkbox
              checked={!!field.value}
              onCheckedChange={(v) => field.onChange(v === true)}
            />
          )}
        />
        Primary contact
      </label>

      {mutation.isError && (
        <p className="text-xs text-destructive">Failed to update contact.</p>
      )}

      <div className="flex gap-2">
        <Button type="submit" size="sm" disabled={mutation.isPending}>
          <Check className="size-3.5" />
          {mutation.isPending ? 'Saving…' : 'Save'}
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={onCancel}>
          <X className="size-3.5" />
          Cancel
        </Button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// VendorContactList
// ---------------------------------------------------------------------------

export default function VendorContactList({ contacts, vendorId }: Props) {
  const queryClient = useQueryClient();
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showInactive, setShowInactive] = useState(false);

  const activeContacts = contacts.filter((c) => c.deactivatedAt === null);
  const inactiveContacts = contacts.filter((c) => c.deactivatedAt !== null);

  const deactivateMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await client.api['vendor-contacts'][':id'].deactivate.$post({
        param: { id },
      });
      if (!res.ok) throw new Error('Failed to deactivate contact');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendor', vendorId] });
    },
  });

  const reactivateMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await client.api['vendor-contacts'][':id'].reactivate.$post({
        param: { id },
      });
      if (!res.ok) throw new Error('Failed to reactivate contact');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendor', vendorId] });
    },
  });

  const setPrimaryMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await client.api['vendor-contacts'][':id'].$put({
        param: { id },
        json: { isPrimary: true },
      });
      if (!res.ok) throw new Error('Failed to set primary');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendor', vendorId] });
    },
  });

  return (
    <div className="space-y-3">
      {/* Active contacts */}
      {activeContacts.length === 0 && !showAdd && (
        <p className="text-sm text-muted-foreground">No contacts yet.</p>
      )}

      {activeContacts.map((contact) => (
        <div key={contact.id}>
          {editingId === contact.id ? (
            <EditContactForm
              contact={contact}
              vendorId={vendorId}
              onCancel={() => setEditingId(null)}
            />
          ) : (
            <div className="flex items-start justify-between gap-3 rounded-xl border border-border bg-card px-4 py-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium leading-snug">{contact.name}</span>
                  {contact.isPrimary && (
                    <Star className="size-3.5 fill-amber-400 text-amber-400" />
                  )}
                  {contact.role && (
                    <span className="text-xs text-muted-foreground">{contact.role}</span>
                  )}
                </div>
                <div className="mt-0.5 flex flex-wrap gap-x-3 text-xs text-muted-foreground">
                  {contact.email && <span>{contact.email}</span>}
                  {contact.phone && <span>{contact.phone}</span>}
                </div>
              </div>

              <div className="flex shrink-0 items-center gap-1">
                {!contact.isPrimary && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 px-2 text-xs"
                    onClick={() => setPrimaryMutation.mutate(contact.id)}
                    disabled={setPrimaryMutation.isPending}
                    title="Set as primary contact"
                  >
                    <Star className="size-3.5" />
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 px-2"
                  onClick={() => setEditingId(contact.id)}
                  title="Edit contact"
                >
                  <Pencil className="size-3.5" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 px-2 text-xs text-muted-foreground hover:text-destructive"
                  onClick={() => deactivateMutation.mutate(contact.id)}
                  disabled={deactivateMutation.isPending}
                  title="Deactivate contact"
                >
                  <X className="size-3.5" />
                </Button>
              </div>
            </div>
          )}
        </div>
      ))}

      {/* Add form */}
      {showAdd && (
        <AddContactForm vendorId={vendorId} onCancel={() => setShowAdd(false)} />
      )}

      {/* Add button */}
      {!showAdd && (
        <Button
          size="sm"
          variant="outline"
          className="gap-1.5"
          onClick={() => setShowAdd(true)}
        >
          <UserPlus className="size-3.5" />
          Add contact
        </Button>
      )}

      {/* Inactive contacts toggle */}
      {inactiveContacts.length > 0 && (
        <div className="pt-1">
          <button
            type="button"
            className="text-xs text-muted-foreground underline decoration-dashed underline-offset-4 hover:text-foreground"
            onClick={() => setShowInactive((v) => !v)}
          >
            {showInactive
              ? 'Hide inactive contacts'
              : `Show ${inactiveContacts.length} inactive contact${inactiveContacts.length > 1 ? 's' : ''}`}
          </button>

          {showInactive && (
            <div className="mt-2 space-y-2">
              {inactiveContacts.map((contact) => (
                <div
                  key={contact.id}
                  className="flex items-start justify-between gap-3 rounded-xl border border-border bg-muted/40 px-4 py-3 opacity-60"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium leading-snug line-through">{contact.name}</span>
                      {contact.role && (
                        <span className="text-xs text-muted-foreground">{contact.role}</span>
                      )}
                    </div>
                    <div className="mt-0.5 flex flex-wrap gap-x-3 text-xs text-muted-foreground">
                      {contact.email && <span>{contact.email}</span>}
                      {contact.phone && <span>{contact.phone}</span>}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 shrink-0 px-2 text-xs"
                    onClick={() => reactivateMutation.mutate(contact.id)}
                    disabled={reactivateMutation.isPending}
                  >
                    Reactivate
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
